import { FC } from "react";
import { observer } from "mobx-react";
import { makeAutoObservable } from "mobx";
import { failure, isPending, ok, pending, Result } from "@/common/common";
import { AxiosInstance } from "axios";
import UserStore from "@/stores/UserStore";
import { Dialog } from "@/components-v2/Dialog";
import { TMessage } from "@/i18n/tt";
import { isMobileView } from "@/utils/screen";
import { Markdown } from "@/text/Markdown";
import { useTranslation } from "@/stores/RootStoreContext";

export class AcceptCompanyTermsDialogModel {
  readonly http: AxiosInstance;
  readonly userStore: UserStore;
  readonly closeDialog: () => void;
  readonly onDecline: () => void;

  acceptResult?: Result<void, TMessage>;

  constructor(
    http: AxiosInstance,
    userStore: UserStore,
    closeDialog: () => void,
    onDecline: () => void,
  ) {
    this.http = http;
    this.userStore = userStore;
    this.closeDialog = closeDialog;
    this.onDecline = onDecline;
    makeAutoObservable(
      this,
      { http: false, closeDialog: false, onDecline: false, userStore: false },
      { autoBind: true },
    );
  }

  *accept() {
    try {
      this.acceptResult = pending();
      yield this.userStore.acceptCompanyTerms();
      this.acceptResult = ok(undefined);
      this.closeDialog();
    } catch (e) {
      this.acceptResult = failure({ key: "terms-of-service.failed-to-accept" });
    }
  }
}

export const AcceptCompanyTermsDialogView: FC<{ model: AcceptCompanyTermsDialogModel }> = observer(
  ({ model }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("terms-of-service.company-terms-title")}
        submitLabel={t("terms-of-service.accept-and-continue")}
        cancelLabel={t("decline")}
        onSubmit={() => model.accept()}
        onCancel={model.onDecline}
        isSubmitting={isPending(model.acceptResult)}
        fullscreen={isMobileView()}
      >
        <Markdown value={t("terms-of-service.updated-terms")} enableLinks disableLastMargin />
      </Dialog>
    );
  },
);
